import { ApolloClient, ApolloProvider, from, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { CubeApi } from "@cubejs-client/core";
import { CubeProvider } from "@cubejs-client/react";
import { Environment } from "@lyfegen-platform/graphql-schema/models";
import { LyfeTheme } from "@lyfegen-platform/react-common";
import { CssBaseline } from "@material-ui/core";
import { useEffect } from "react";
import { useIdleTimer } from "react-idle-timer";
import { BrowserRouter } from "react-router-dom";
import { useSetRecoilState } from "recoil";
import { RootSubscriptions } from "./components/RootSubscriptions";
import Translation from "./components/Translation";
import { SENTRY_ENV_NOT_SET } from "./config";
import { link } from "./constants/apollo-link";
import AppBackdropContainer, { expirationEventName } from "./containers/layout/AppBackdropContainer";
import UserLeaveConfirmation from "./containers/layout/userConfirmOpen";
import { Mutation, Query } from "./models/generated/graphql";
import IUserDataProps from "./models/identity/user-data";
import MainRoutes from "./routes/MainRoutes";
import { AuthenticationService } from "./services/authentication.service";
import { bannerState, navlinUrlState } from "./store/layout";
import ToastNavigationListener from "./utils/toast-navigation-listener";

const graphQlFieldsWithCustomErrorProcessing: Array<keyof Mutation | keyof Query> = [
	"AddAgreementDecision",
	"GetUserFieldsForUpload",
	"SaveSimulation",
	"UpdateSimulationBusinessCase",
	"UpdateUserFiltersSimulations",
	"DeleteSimulation",
	"SaveSimulation",
	"UpdateSimulationUom",
	"UpdateSimulatorRole",
	"UpdateDomain",
	"DeleteDomain",
	"CreateDomain",
	"AssignUserToDomain",
	"RevokeRecord",
	"DeleteFlexEntity",
	"SaveReviewedInvoice",
	"SaveAdjustedAmount",
	"CreateDocumentMessage",
	"SendDocumentToSmartMip",
	"SendDecisionsToSmartMip",
];

// 30mins idle timeout
const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const IDLE_CHECK_THROTTLE = 1000;

// 8h max session length
const CLIENT_SIDE_SESSION_TIMEOUT_MS = 8 * 60 * 60 * 1000;
const CLIENT_SIDE_SESSION_THROTTLE = 10000;

interface AppProps {
	user: IUserDataProps;
	handleLogout: () => any;
	identityServerUrl: string;
	authService: AuthenticationService;
	cubeApi: CubeApi;
	translationsPath: string;
	serverEnvironment: Environment;
	navlinUrl: string;
	sentryEnv: Environment | typeof SENTRY_ENV_NOT_SET;
}

const App: React.FC<AppProps> = ({
	user,
	handleLogout,
	identityServerUrl,
	authService,
	cubeApi,
	translationsPath,
	serverEnvironment,
	navlinUrl,
	sentryEnv,
}) => {
	const { start, getElapsedTime } = useIdleTimer({
		onIdle: handleLogout,
		timeout: IDLE_TIMEOUT_MS,
		throttle: IDLE_CHECK_THROTTLE,
		eventsThrottle: IDLE_CHECK_THROTTLE,
	});

	start();

	useEffect(() => {
		const checkActivityInterval = setInterval(
			() => getElapsedTime().valueOf() > CLIENT_SIDE_SESSION_TIMEOUT_MS && handleLogout(),
			CLIENT_SIDE_SESSION_THROTTLE
		);

		return () => clearInterval(checkActivityInterval);
	}, []);

	const dialogText = {
		continue: "yes-leave",
		discard: "no-stay",
		question: "sure-leave",
	};

	function setUserLeaveConfirmation(message: string, callback: (ok: boolean) => void) {
		const text = {
			...dialogText,
			reason: message,
		};
		return UserLeaveConfirmation(text, callback);
	}

	const authLink = setContext(async (_, { headers }) => {
		const apiKey = (await authService.getUser())?.access_token;

		return {
			headers: {
				...headers,
				Authorization: `Bearer ${apiKey}`,
			},
		};
	});

	const setBanner = useSetRecoilState(bannerState);
	const setNavlinUrl = useSetRecoilState(navlinUrlState);

	useEffect(() => setNavlinUrl(navlinUrl), [navlinUrl, setNavlinUrl]);

	// Log any GraphQL errors or network error that occurred
	const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
		const operationName = operation.operationName as keyof Mutation | keyof Query;
		if (networkError && (networkError as any).statusCode == 401) {
			window.dispatchEvent(new Event(expirationEventName));
			return;
		}

		if (graphQlFieldsWithCustomErrorProcessing.includes(operationName)) {
			return;
		}
		if (graphQLErrors || networkError) {
			graphQLErrors && console.error(graphQLErrors);
			networkError && console.error(networkError);
			const bannerError =
				networkError ?? (graphQLErrors?.length ? new Error(graphQLErrors.map((e) => e.message).join("; ")) : undefined);
			setBanner({ isError: true, error: bannerError, operation: operationName, variables: operation.variables });
		}
	});

	const client = new ApolloClient({
		link: from([errorLink, authLink.concat(link(authService))]),
		cache: new InMemoryCache(),
		connectToDevTools: true,
	});

	const languages = {
		en: sessionStorage.getItem("en"),
		de: sessionStorage.getItem("de"),
	};

	return (
		<ApolloProvider client={client}>
			<LyfeTheme app="platform-new">
				<CubeProvider cubeApi={cubeApi}>
					<Translation
						preferredLanguage={user.preferredLanguage}
						languages={languages}
						translationsPath={translationsPath}
						serverEnvironment={serverEnvironment}
					>
						<RootSubscriptions tenantId={user.organization?.externalId || ""} userId={user.id || ""} />
						<CssBaseline />
						<BrowserRouter getUserConfirmation={setUserLeaveConfirmation}>
							<MainRoutes
								user={user}
								handleLogout={handleLogout}
								identityServerUrl={identityServerUrl}
								authService={authService}
								serverEnvironment={serverEnvironment}
								sentryEnv={sentryEnv}
							/>
							<ToastNavigationListener />
						</BrowserRouter>
						<AppBackdropContainer />
					</Translation>
				</CubeProvider>
			</LyfeTheme>
		</ApolloProvider>
	);
};

export default App;
